// solo/Clients.jsx - Client management with patch test tracking
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Chip,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Science as ScienceIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../axiosConfig';

const Clients = () => {
  // State
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [patchTestDialogOpen, setPatchTestDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [viewTab, setViewTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Client form state
  const [clientForm, setClientForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    notes: '',
    allergies: '',
    preferences: ''
  });
  
  // Patch test form state
  const [patchTestForm, setPatchTestForm] = useState({
    test_date: new Date().toISOString().split('T')[0],
    product_type: '',
    product_brand: '',
    result: 'pending',
    administered_by: '',
    notes: ''
  });
  
  const [formErrors, setFormErrors] = useState({});

  // Fetch clients
  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = searchQuery ? { search: searchQuery } : {};
      const response = await api.get('/clients', { params });
      setClients(response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      showSnackbar('Failed to load clients', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchClients();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Snackbar helper
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Get patch test status chip
  const getPatchTestChip = (status) => {
    const configs = {
      valid: { label: 'Valid', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
      expired: { label: 'Expired', color: 'error', icon: <WarningIcon fontSize="small" /> },
      pending: { label: 'Pending', color: 'warning', icon: <ScheduleIcon fontSize="small" /> },
      positive: { label: 'Positive', color: 'error', icon: <CancelIcon fontSize="small" /> },
      none: { label: 'None', color: 'default', icon: null }
    };
    const config = configs[status] || configs.none;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  // Client dialog handlers
  const handleOpenClientDialog = (client = null) => {
    if (client) {
      setClientForm({
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        phone: client.phone || '',
        email: client.email || '',
        notes: client.notes || '',
        allergies: client.allergies || '',
        preferences: client.preferences || ''
      });
      setSelectedClient(client);
    } else {
      setClientForm({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        notes: '',
        allergies: '',
        preferences: ''
      });
      setSelectedClient(null);
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseClientDialog = () => {
    setDialogOpen(false);
    setSelectedClient(null);
    setFormErrors({});
  };

  // View client details
  const handleViewClient = async (client) => {
    try {
      const response = await api.get(`/clients/${client.id}`);
      setSelectedClient(response.data);
      setViewTab(0);
      setViewDialogOpen(true);
    } catch (error) {
      showSnackbar('Failed to load client details', 'error');
    }
  };

  // Client form change
  const handleClientFormChange = (e) => {
    const { name, value } = e.target;
    setClientForm(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validate client form
  const validateClientForm = () => {
    const errors = {};
    if (!clientForm.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit client form
  const handleSubmitClient = async () => {
    if (!validateClientForm()) return;
    
    try {
      if (selectedClient?.id) {
        await api.put(`/clients/${selectedClient.id}`, clientForm);
        showSnackbar('Client updated successfully!');
      } else {
        await api.post('/clients', clientForm);
        showSnackbar('Client created successfully!');
      }
      handleCloseClientDialog();
      fetchClients();
    } catch (error) {
      if (error.response?.status === 409) {
        showSnackbar('A client with this name and contact already exists', 'error');
      } else {
        showSnackbar(error.response?.data?.error || 'Failed to save client', 'error');
      }
    }
  };

  // Delete client
  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await api.delete(`/clients/${clientId}`);
      showSnackbar('Client deleted');
      fetchClients();
      setViewDialogOpen(false);
    } catch (error) {
      showSnackbar('Failed to delete client', 'error');
    }
  };

  // Patch test dialog handlers
  const handleOpenPatchTestDialog = () => {
    setPatchTestForm({
      test_date: new Date().toISOString().split('T')[0],
      product_type: '',
      product_brand: '',
      result: 'pending',
      administered_by: '',
      notes: ''
    });
    setPatchTestDialogOpen(true);
  };

  // Patch test form change
  const handlePatchTestFormChange = (e) => {
    const { name, value } = e.target;
    setPatchTestForm(prev => ({ ...prev, [name]: value }));
  };

  // Submit patch test
  const handleSubmitPatchTest = async () => {
    if (!patchTestForm.test_date) {
      showSnackbar('Test date is required', 'error');
      return;
    }
    
    try {
      await api.post('/patch-tests', {
        client_id: selectedClient.id,
        ...patchTestForm
      });
      showSnackbar('Patch test recorded!');
      setPatchTestDialogOpen(false);
      // Refresh client details
      const response = await api.get(`/clients/${selectedClient.id}`);
      setSelectedClient(response.data);
      fetchClients();
    } catch (error) {
      showSnackbar(error.response?.data?.error || 'Failed to save patch test', 'error');
    }
  };

  // Update patch test result
  const handleUpdatePatchTestResult = async (testId, result) => {
    try {
      await api.put(`/patch-tests/${testId}`, { result });
      showSnackbar(`Patch test marked as ${result}`);
      // Refresh client details
      const response = await api.get(`/clients/${selectedClient.id}`);
      setSelectedClient(response.data);
      fetchClients();
    } catch (error) {
      showSnackbar('Failed to update patch test', 'error');
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box 
        sx={{ 
          bgcolor: 'white', 
          borderBottom: '1px solid #e0e0e0',
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="h6" fontWeight="600">
          Clients
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={fetchClients} size="small">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenClientDialog()}
          >
            New Client
          </Button>
        </Box>
      </Box>

      {/* Search Bar */}
      <Box sx={{ p: 2, pb: 0 }}>
        <TextField
          fullWidth
          placeholder="Search clients by name, phone, or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          sx={{ bgcolor: 'white' }}
        />
      </Box>

      {/* Clients Table */}
      <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
        <Paper sx={{ p: 2 }}>
          {loading ? (
            <Typography>Loading clients...</Typography>
          ) : clients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {searchQuery ? 'No clients found' : 'No clients yet'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {searchQuery ? 'Try a different search' : 'Add your first client to get started'}
              </Typography>
              {!searchQuery && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenClientDialog()}
                >
                  Add First Client
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Phone</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Patch Test</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow 
                      key={client.id} 
                      hover 
                      onClick={() => handleViewClient(client)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {client.full_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{client.phone || '-'}</TableCell>
                      <TableCell>{client.email || '-'}</TableCell>
                      <TableCell>
                        {getPatchTestChip(client.patch_test_status)}
                        {client.days_until_patch_expiry !== null && client.days_until_patch_expiry <= 30 && (
                          <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                            Expires in {client.days_until_patch_expiry} days
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenClientDialog(client)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClient(client.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Create/Edit Client Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseClientDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedClient?.id ? 'Edit Client' : 'New Client'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={clientForm.first_name}
                onChange={handleClientFormChange}
                error={!!formErrors.first_name}
                helperText={formErrors.first_name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={clientForm.last_name}
                onChange={handleClientFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={clientForm.phone}
                onChange={handleClientFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={clientForm.email}
                onChange={handleClientFormChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Allergies / Sensitivities"
                name="allergies"
                value={clientForm.allergies}
                onChange={handleClientFormChange}
                multiline
                rows={2}
                placeholder="Any known allergies or skin sensitivities"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Preferences"
                name="preferences"
                value={clientForm.preferences}
                onChange={handleClientFormChange}
                multiline
                rows={2}
                placeholder="Hair type, preferred products, styling preferences, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={clientForm.notes}
                onChange={handleClientFormChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClientDialog}>Cancel</Button>
          <Button onClick={handleSubmitClient} variant="contained">
            {selectedClient?.id ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Client Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{selectedClient?.full_name}</Typography>
            {selectedClient && getPatchTestChip(selectedClient.patch_test_status)}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs value={viewTab} onChange={(e, v) => setViewTab(v)} sx={{ mb: 2 }}>
            <Tab label="Details" icon={<PersonIcon />} iconPosition="start" />
            <Tab label="Patch Tests" icon={<ScienceIcon />} iconPosition="start" />
          </Tabs>
          
          {viewTab === 0 && selectedClient && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{selectedClient.phone || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{selectedClient.email || '-'}</Typography>
              </Grid>
              {selectedClient.allergies && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">Allergies / Sensitivities</Typography>
                    <Typography variant="body2">{selectedClient.allergies}</Typography>
                  </Alert>
                </Grid>
              )}
              {selectedClient.preferences && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Preferences</Typography>
                  <Typography variant="body1">{selectedClient.preferences}</Typography>
                </Grid>
              )}
              {selectedClient.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                  <Typography variant="body1">{selectedClient.notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
          
          {viewTab === 1 && selectedClient && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">Patch Test History</Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenPatchTestDialog}
                >
                  Record Patch Test
                </Button>
              </Box>
              
              {selectedClient.patch_tests?.length === 0 ? (
                <Alert severity="info">
                  No patch tests recorded for this client. Record one before booking colour services.
                </Alert>
              ) : (
                <List>
                  {selectedClient.patch_tests?.map((test) => (
                    <Paper key={test.id} sx={{ mb: 2, p: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                          <Typography variant="body1">
                            {new Date(test.test_date).toLocaleDateString('en-GB')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle2" color="text.secondary">Product</Typography>
                          <Typography variant="body1">
                            {test.product_type || 'General'}
                            {test.product_brand && ` (${test.product_brand})`}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Typography variant="subtitle2" color="text.secondary">Result</Typography>
                          <Chip
                            label={test.result}
                            color={test.result === 'negative' ? 'success' : test.result === 'positive' ? 'error' : 'warning'}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Typography variant="subtitle2" color="text.secondary">Expires</Typography>
                          <Typography variant="body2">
                            {test.expiry_date 
                              ? new Date(test.expiry_date).toLocaleDateString('en-GB')
                              : '-'
                            }
                          </Typography>
                          {test.is_expired && (
                            <Chip label="Expired" color="error" size="small" />
                          )}
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          {test.result === 'pending' && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                color="success"
                                variant="outlined"
                                onClick={() => handleUpdatePatchTestResult(test.id, 'negative')}
                              >
                                Clear
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() => handleUpdatePatchTestResult(test.id, 'positive')}
                              >
                                Reaction
                              </Button>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                      {test.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {test.notes}
                        </Typography>
                      )}
                      {test.administered_by && (
                        <Typography variant="caption" color="text.secondary">
                          Administered by: {test.administered_by}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Button 
            color="error" 
            onClick={() => handleDeleteClient(selectedClient?.id)}
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button 
              variant="contained" 
              startIcon={<EditIcon />}
              onClick={() => {
                setViewDialogOpen(false);
                handleOpenClientDialog(selectedClient);
              }}
            >
              Edit
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Record Patch Test Dialog */}
      <Dialog open={patchTestDialogOpen} onClose={() => setPatchTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Record Patch Test - {selectedClient?.full_name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Test Date"
                name="test_date"
                type="date"
                value={patchTestForm.test_date}
                onChange={handlePatchTestFormChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Product Type"
                name="product_type"
                value={patchTestForm.product_type}
                onChange={handlePatchTestFormChange}
                placeholder="e.g., Colour, Bleach, Perm"
                select
                SelectProps={{ native: true }}
              >
                <option value="">Select or type...</option>
                <option value="Colour">Colour</option>
                <option value="Bleach">Bleach</option>
                <option value="Tint">Tint</option>
                <option value="Perm">Perm</option>
                <option value="Relaxer">Relaxer</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Product Brand"
                name="product_brand"
                value={patchTestForm.product_brand}
                onChange={handlePatchTestFormChange}
                placeholder="e.g., Wella, L'Oreal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Result"
                name="result"
                value={patchTestForm.result}
                onChange={handlePatchTestFormChange}
                select
                SelectProps={{ native: true }}
              >
                <option value="pending">Pending (wait 48hrs)</option>
                <option value="negative">Negative (clear)</option>
                <option value="positive">Positive (reaction)</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Administered By"
                name="administered_by"
                value={patchTestForm.administered_by}
                onChange={handlePatchTestFormChange}
                placeholder="Staff member name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={patchTestForm.notes}
                onChange={handlePatchTestFormChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                A patch test must be at least 48 hours before any colour appointment. 
                The test will be valid for 12 months by default.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPatchTestDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmitPatchTest} variant="contained">
            Record Test
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Clients;